/** What a sharing agreement lets a buyer do, and **why it does not** - ADR-0160.
 *
 * Separate from the components (like `supplierLabels.ts`) so the two screens that need the answer
 * cannot word it differently, and so a fast-refresh boundary stays a boundary.
 *
 * ## Mirrors the server's `permits`, and the server decides
 *
 * `suppliers/service.py::permits` is the rule: an agreement permits a use **on the date the figure is
 * for** (the answered request's `period_end`, not today) when it is not revoked, has begun, has not
 * ended, and names the use. `accept` refuses with one sentence for all of them; this reads the same
 * list the screen already holds and says which of the four stopped each agreement, so the buyer is
 * told whom to ask for what instead of meeting a refusal after choosing a site.
 * Nothing here grants or widens anything: a screen that guessed "yes" is corrected by the server's 422,
 * and one that says "no" for a row the server would accept is the defect, so the order of the checks
 * is the server's.
 */

import type { Agreement, Exchange } from "../api/suppliers";

/** The ways an agreement can fail to permit a copy, in the order the server tests them. */
export type Shortfall = "revoked" | "not_yet" | "ended" | "read_only";

export interface Assessed {
  agreement: Agreement;
  /** `null` when the agreement permits an inventory copy for that period. */
  shortfall: Shortfall | null;
}

/** One agreement against the date a figure is for. Dates are ISO `YYYY-MM-DD`, which order as strings. */
export function assess(agreement: Agreement, on: string): Assessed {
  let shortfall: Shortfall | null = null;
  if (agreement.revoked_at !== null) shortfall = "revoked";
  else if (on < agreement.effective_from) shortfall = "not_yet";
  else if (agreement.expires_on !== null && on > agreement.expires_on) shortfall = "ended";
  else if (!agreement.permitted_uses.includes("inventory_copy")) shortfall = "read_only";
  return { agreement, shortfall };
}

/** What this supplier has granted this workspace, split into the ones that permit a copy for the
 * answered request's period and the ones that do not (each with its one reason). */
export function copyPermission(
  agreements: readonly Agreement[],
  row: Pick<Exchange, "supplier_org_id" | "period_end">,
): { usable: Agreement[]; refused: Assessed[] } {
  const usable: Agreement[] = [];
  const refused: Assessed[] = [];
  for (const agreement of agreements) {
    if (agreement.supplier_org_id !== row.supplier_org_id || agreement.i_am !== "buyer") continue;
    const assessed = assess(agreement, row.period_end);
    if (assessed.shortfall === null) usable.push(agreement);
    else refused.push(assessed);
  }
  return { usable, refused };
}

/** The sentence for one shortfall. Names the date the figure is for, because "expired" without it
 * reads as a statement about today. */
export function shortfallSentence(
  assessed: Assessed,
  periodEnd: string,
  shortDate: (iso: string) => string,
): string {
  const { agreement } = assessed;
  switch (assessed.shortfall) {
    case "revoked":
      return `Revoked${agreement.revoked_reason ? ` (${agreement.revoked_reason})` : ""}. A revoked permission stops the next copy.`;
    case "not_yet":
      return `Begins ${shortDate(agreement.effective_from)}, after the ${shortDate(periodEnd)} this answer is for. The check is dated on the period, not on today.`;
    case "ended":
      return `Ended ${agreement.expires_on ? shortDate(agreement.expires_on) : ""}, before the ${shortDate(periodEnd)} this answer is for. The check is dated on the period, not on today.`;
    case "read_only":
      return "Lets you read the answer and does not permit copying it into an inventory. The two are separate permissions.";
    case null:
      return "Permits a copy.";
  }
}

/** The customers that have asked this workspace for a figure, each once, in the exchange's order,
 * with how many times. **Only rows where this workspace is the supplier**: on the other side the
 * counterparty is a supplier, and permitting a supplier to copy your figures is not a thing. */
export function knownBuyers(rows: readonly Exchange[]): Array<{ id: string; asked: number }> {
  const counts = new Map<string, number>();
  for (const row of rows) {
    if (row.i_am !== "supplier") continue;
    counts.set(row.buyer_org_id, (counts.get(row.buyer_org_id) ?? 0) + 1);
  }
  return [...counts].map(([id, asked]) => ({ id, asked }));
}

/** The live (not revoked) agreement this workspace granted that buyer, if any. The server refuses a
 * second one, whatever its dates: revoke first, so the record says what was permitted when. */
export function liveGrantTo(agreements: readonly Agreement[], buyerId: string): Agreement | undefined {
  return agreements.find(
    (candidate) =>
      candidate.i_am === "supplier" && candidate.buyer_org_id === buyerId && candidate.revoked_at === null,
  );
}
